// @ts-check
/**
 * The errors of `@smotaal/pack-fs`, in one place: a consumer that cares about
 * the package failing imports them together, and no other module of the
 * package defines an `Error` class.
 *
 * @module @smotaal/pack-fs/errors
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1 (code generation)
 */

// ---------------------------------------------------------------------------
// The folder contract
// ---------------------------------------------------------------------------

/**
 * The one "not there". Its `name` is what a `DOMException` of the same
 * meaning carries, so a caller can test `error.name` without caring which
 * medium raised it.
 */
export class NotFoundError extends Error {
    /** @param {string} at */
    constructor(at) {
        super(`Not found: “${at}”`);
        this.name = 'NotFoundError';
        this.path = at;
    }
}

/** A write asked of a folder that only reads. */
export class ReadOnlyError extends Error {
    /** @param {string} what */
    constructor(what) {
        super(`This folder is read-only — ${what}`);
        this.name = 'ReadOnlyError';
    }
}

// ---------------------------------------------------------------------------
// The pack format
// ---------------------------------------------------------------------------

/** A pack, a collection or a document that does not read as one. */
export class PackFormatError extends Error {
    /** @param {string} message */
    constructor(message) {
        super(message);
        this.name = 'PackFormatError';
    }
}

// ---------------------------------------------------------------------------
// Locators
// ---------------------------------------------------------------------------

/** A locator that does not parse, or names a scheme nothing provides. */
export class LocatorError extends Error {
    /** @param {string} message */
    constructor(message) {
        super(message);
        this.name = 'LocatorError';
    }
}

// ---------------------------------------------------------------------------
// Providers
// ---------------------------------------------------------------------------

/** Something a provider refuses, with the reason. */
export class ProviderError extends Error {
    /** @param {string} message */
    constructor(message) {
        super(message);
        this.name = 'ProviderError';
    }
}

/** A write asked of a provider that only reads — refused before a byte is sent. */
export class ReadOnlyProviderError extends ProviderError {
    /** @param {string} message */
    constructor(message) {
        super(message);
        this.name = 'ReadOnlyProviderError';
    }
}

/** A member whose bytes disagree with what the manifest records for it (A3, R12). */
export class MemberRefusedError extends ProviderError {
    /**
     * @param {string} name
     * @param {string} why
     */
    constructor(name, why) {
        super(`“${name}” is refused — ${why}`);
        this.name = 'MemberRefusedError';
        this.member = name;
    }
}

/** The incarnation on the medium is not the one the caller expected (`HTTP.md` §5). */
export class IncarnationMismatchError extends ProviderError {
    /**
     * @param {string} expected
     * @param {string} actual
     */
    constructor(expected, actual) {
        super(`This pack is at incarnation “${actual}” and the change was made against “${expected}” — ` +
            'nothing was written, because two producers must not clobber each other');
        this.name = 'IncarnationMismatchError';
        this.expected = expected;
        this.actual = actual;
    }
}

// ---------------------------------------------------------------------------
// The test harness
// ---------------------------------------------------------------------------

/**
 * What the harness's built-in assertions throw. The four properties a Node
 * reporter reads — `name`, `actual`, `expected`, `operator` — are the ones a
 * Node `AssertionError` carries, so a failure raised in a page prints the same
 * way as a failure raised in a test file.
 */
export class AssertionError extends Error {
    /**
     * @param {{
     *   message: string,
     *   actual?: any,
     *   expected?: any,
     *   operator: string,
     *   generatedMessage?: boolean,
     * }} failure
     */
    constructor({ message, actual, expected, operator, generatedMessage = false }) {
        super(message);
        this.name = 'AssertionError';
        this.code = 'ERR_ASSERTION';
        this.actual = actual;
        this.expected = expected;
        this.operator = operator;
        this.generatedMessage = generatedMessage;
    }
}
